import React, { useContext, useEffect, useReducer } from 'react';
import api from '../api';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import { Store } from '../Store';
import { getError } from '../utils';
import Button from 'react-bootstrap/Button';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatEuro, formatDate } from '../utils';
import Pagination from '../components/general/Pagination';
import { Container, Table } from 'react-bootstrap';

const reducer = (state, action) => {
  // reducer function to update the state for different cases
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        orders: action.payload.orders,
        page: action.payload.page,
        pages: action.payload.pages,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

export default function OrderHistoryScreen() {
  const { t } = useTranslation();
  const { state } = useContext(Store); // useContext gives access to the state of the context (in this case the Store)
  const { userInfo } = state; // gets the userInfo out of the application state
  const navigate = useNavigate(); // enables the functionality, to navigate a user to a different screen

  const { search } = useLocation(); // gets the query parameters of the URL and saves them in the variable called 'search'
  const sp = new URLSearchParams(search); // creates a new instance of the URLSearchParams class, passing in the search string, wich allows to apply several methods to work with the query parameters
  const page = sp.get('page') || 1; // Retrieves the value of the 'page' query parameter (the number on wich page the user is on)

  const [{ loading, error, orders, pages }, dispatch] = useReducer(reducer, {
    // default value:
    loading: true,
    error: '',
    orders: [],
  }); // useReducer function instead of useState hook, to manage complex state. Gets the states (loading and error) and dispatch, to dispatch the actions from the reducer above

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });
      try {
        const { data } = await api.get(`/api/orders/mine?page=${page}`); // authorized axios request to the `/api/orders/mine` api, to get the orders of the current user

        const userOrderData = data.orders;

        const updatedUserOrderData = userOrderData.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        ); // Sorts orders by createdAt in descending order (newest first)

        dispatch({
          type: 'FETCH_SUCCESS',
          payload: {
            ...data,
            orders: updatedUserOrderData,
          },
        });
      } catch (error) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(error),
        });
      }
    }; // definition of the fetchData function, that fetches the orders of the current user from the backend
    fetchData(); // execution of the fetchData function
  }, [userInfo, page]);

  return (
    <Container fluid="xl" className="order-history-container">
      <title>{t('order-history-screen-helmet')}</title>

      <h1 className="screen-heading">{t('order-history-screen-heading')}</h1>

      {loading ? (
        <div className="overlay">
          <LoadingLogo />
        </div>
      ) : error ? (
        <MessageBox className="danger-alert">{error}</MessageBox>
      ) : orders.length === 0 ? (
        <MessageBox className="default-alert">
          {t('order-history-screen-no-orders-message')}
        </MessageBox>
      ) : (
        <div>
          <Table className="order-history-table" hover size="sm">
            <thead>
              <tr>
                <th className="order-id">
                  {t('order-history-screen-table-heading-1')}
                </th>
                <th>{t('order-history-screen-table-heading-2')}</th>
                <th>{t('order-history-screen-table-heading-3')}</th>
                <th className="shipped-at">
                  {t('order-history-screen-table-heading-4')}
                </th>
                <th>{t('order-history-screen-table-heading-5')}</th>
              </tr>
            </thead>

            <tbody>
              {orders.map((order) => (
                <tr key={order.orderId}>
                  <td className="order-id">{order.orderId}</td>

                  <td>{formatDate(order.createdAt)}</td>

                  <td>{formatEuro(order.totalGross)}</td>

                  <td className="shipped-at">
                    {order.status === 'shipped'
                      ? formatDate(order.trackingNumbers[0].date)
                      : '-'}
                  </td>

                  <td>
                    {/* the "order-actions" class is applyed to an additional div inside the <td> tag, as the display: flex makes the borders between the rows misaligned */}
                    <div className="order-actions">
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          navigate(`/order/${order.orderId}`);
                        }}
                      >
                        {t('order-history-screen-details')}
                      </Button>

                      <Button
                        variant="secondary"
                        type="button"
                        className="d-none d-lg-block invoice-button"
                        onClick={async () => {
                          try {
                            const originalInvoice = order.invoices.find(
                              (inv) => inv.type === 'original'
                            ); // Find the original invoice from the order's invoices

                            if (!originalInvoice) {
                              toast.error(
                                t(
                                  'order-history-screen-original-invoice-not-found'
                                )
                              );
                              return;
                            }

                            const response = await api.get(
                              `/api/downloads/invoice/${order.orderId}/${originalInvoice.id}`,
                              {
                                responseType: 'blob', // Ensure the response is treated as binary data
                              }
                            ); // API call to fetch the invoice PDF using orderId and id

                            // Create a Blob and generate a URL for the PDF
                            const blob = new Blob([response.data], {
                              type: 'application/pdf',
                            });
                            const pdfUrl = URL.createObjectURL(blob);

                            // Open the PDF in a new browser tab
                            window.open(pdfUrl, '_blank');

                            // Revoke the URL after opening to clean up resources
                            setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000); // Delay to ensure PDF loads
                          } catch (error) {
                            toast.error(t(getError(error))); // Handle errors and show a toast
                          }
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8.5 6.5a.5.5 0 0 0-1 0v3.793L6.354 9.146a.5.5 0 1 0-.708.708l2 2a.5.5 0 0 0 .708 0l2-2a.5.5 0 0 0-.708-.708L8.5 10.293z" />
                          <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z" />
                        </svg>

                        {order.invoices.some(
                          (invoice) => invoice.type === 'cancellation'
                        )
                          ? t('order-history-screen-original-invoice')
                          : t('order-history-screen-invoice')}
                      </Button>

                      {order.invoices.some(
                        (invoice) => invoice.type === 'cancellation'
                      ) && (
                        <Button
                          variant="secondary"
                          type="button"
                          className="d-none d-lg-block invoice-button"
                          onClick={async () => {
                            try {
                              const cancellationInvoice = order.invoices.find(
                                (inv) => inv.type === 'cancellation'
                              ); // Find the cancellation invoice

                              if (!cancellationInvoice) {
                                toast.error(
                                  t(
                                    'order-history-screen-cancellation-invoice-not-found'
                                  )
                                );
                                return;
                              }

                              const response = await api.get(
                                `/api/downloads/invoice/${order.orderId}/${cancellationInvoice.id}`,
                                {
                                  responseType: 'blob', // Ensure the response is treated as binary data
                                }
                              ); // Fetch the cancellation invoice PDF

                              // Create a Blob and generate a URL for the PDF
                              const blob = new Blob([response.data], {
                                type: 'application/pdf',
                              });
                              const pdfUrl = URL.createObjectURL(blob);

                              // Open the PDF in a new browser tab
                              window.open(pdfUrl, '_blank');

                              // Revoke the URL after opening to clean up resources
                              setTimeout(
                                () => URL.revokeObjectURL(pdfUrl),
                                1000
                              ); // Delay to ensure PDF loads
                            } catch (error) {
                              toast.error(t(getError(error))); // Handle errors and show a toast
                            }
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 16 16"
                          >
                            <path d="M8.5 6.5a.5.5 0 0 0-1 0v3.793L6.354 9.146a.5.5 0 1 0-.708.708l2 2a.5.5 0 0 0 .708 0l2-2a.5.5 0 0 0-.708-.708L8.5 10.293z" />
                            <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z" />
                          </svg>

                          {t('order-history-screen-invoice-correction')}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Pagination
            pages={pages}
            page={Number(page)}
            baseUrl="/orderhistory"
            search={null}
          />
        </div>
      )}
    </Container>
  );
}
